import { Img } from "@/components/site/Img";
import { Phone, FileText, Wrench } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import { ReserveModal } from "@/components/site/ReserveModal";
import { useSmsHref } from "@/components/site/ContactButtons";
import { SMS_DRAFTS } from "@/lib/contact";
import { track } from "@/lib/track";

export const repairPhotos = [
  {
    src: "/images/repair-before.jpg",
    caption: "The old tile, failing",
    alt: "Commercial fountain with aging blue tile before restoration",
  },
  {
    src: "/images/repair-demo.jpg",
    caption: "Old tile comes off",
    alt: "Old tile being removed from the fountain basin walls",
  },
  {
    src: "/images/repair-1.jpg",
    caption: "Patch & resurface",
    alt: "Patching and resurfacing a cracked commercial fountain basin with fresh concrete",
  },
  {
    src: "/images/repair-2.jpg",
    caption: "Fresh recoat",
    alt: "Fountain basin fully resurfaced with a fresh white coat, ready for color",
  },
  {
    src: "/images/repair-4.jpg",
    caption: "New color going on",
    alt: "New terracotta stain being applied to the restored fountain basin",
  },
  {
    src: "/images/repair-5.jpg",
    caption: "Color built up in coats",
    alt: "Close-up of stain coats building up on the fountain basin wall",
  },
  {
    src: "/images/repair-3.jpg",
    caption: "Finished by hand",
    alt: "Chris hand-finishing the stain on the restored fountain wall",
  },
  {
    src: "/images/repair-smooth.jpg",
    caption: "New surface",
    alt: "Smooth resurfaced fountain basin ready for its new finish",
  },
];

const teaserPhotos = [repairPhotos[0], repairPhotos[3], repairPhotos[6], repairPhotos[7]];

export default function Repairs() {
  const serviceSms = useSmsHref(SMS_DRAFTS.service);
  const [showMaintenance, setShowMaintenance] = useState(false);
  return (
    <section
      id="repairs"
      data-testid="repairs-section"
      className="bg-[#EFEBE1] py-10 md:py-14 border-t border-[#D5CEBD]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-12 md:mb-14">
          <div className="max-w-3xl">
            <div className="section-eyebrow mb-5">
              <span>We Fix Them Too</span>
            </div>
            <h2
              data-testid="repairs-heading"
              className="font-serif font-light text-[#2C2A28] text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-6"
            >
              Repairs and restoration.
            </h2>
            <p className="text-[#5C5955] text-base md:text-lg leading-relaxed max-w-2xl">
              Have a fountain that&rsquo;s seen better days? We repair,
              restore, clean, and maintain older concrete fountains across
              Metro Atlanta, West Georgia, and anywhere Chris can drive to:
              cracked bowls, failed pumps, worn
              finishes, full refinishing in a new color. Most pieces can be
              brought back rather than replaced.
            </p>
            <Link
              to="/fountain-repair"
              data-testid="repairs-learn-more"
              className="mt-6 inline-flex items-center gap-2 text-[#7A6547] hover:text-[#5E4D35] text-sm tracking-[0.22em] uppercase border-b border-[#7A6547]/40 hover:border-[#7A6547] pb-1 transition-colors"
            >
              Fountain repair &amp; restoration&nbsp;→
            </Link>
          </div>
          <div className="flex flex-col gap-3 self-start md:self-auto flex-shrink-0">
            <a
              href={serviceSms}
              onClick={() => track("text_click", "Repairs section")}
              data-testid="repairs-send-photo"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#7A6547] text-[#F9F6F0] text-[11px] tracking-[0.2em] uppercase hover:bg-[#5E4D35] transition-colors whitespace-nowrap"
              aria-label="Text Chris a photo of your fountain"
            >
              Send a photo of your fountain
            </a>
            <a
              href="tel:+16787699652"
              data-testid="repairs-call"
              className="group/call relative inline-flex items-center justify-center gap-2 px-6 py-3.5 border border-[#7A6547] text-[#7A6547] text-[11px] tracking-[0.2em] uppercase hover:bg-[#7A6547] hover:text-[#F9F6F0] transition-all duration-300 overflow-hidden whitespace-nowrap"
              aria-label="Call Chris about a repair at (678) 769-9652"
            >
              <Phone size={13} strokeWidth={1.5} />
              <span className="call-label">Call about a repair</span>
              <span className="call-number tracking-[0.14em]">(678) 769-9652</span>
            </a>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {teaserPhotos.map((p, i) => (
            <figure key={p.src} data-testid={`repairs-photo-${i + 1}`}>
              <div className="zoom-container w-full aspect-[4/5] overflow-hidden bg-[#D5CEBD]">
                <Img
                  src={p.src}
                  alt={p.alt}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  sizes="(min-width: 768px) 25vw, 50vw"
                />
              </div>
              <figcaption className="mt-3 text-center uppercase text-[11px] tracking-[0.18em] text-[#6F6A60]">
                {p.caption}
              </figcaption>
            </figure>
          ))}
        </div>

        <div
          data-testid="maintenance-block"
          className="mt-10 md:mt-12 bg-[#1F1E1C] grain p-5 md:p-6 flex flex-col lg:flex-row lg:items-center gap-5 lg:gap-8"
        >
          <div className="flex-1">
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#C8B387] mb-2">
              Maintenance
            </div>
            <h3 className="font-serif font-light text-[#F9F6F0] text-xl md:text-2xl tracking-tight mb-2">
              Keep it running between visits.
            </h3>
            <p className="text-[#B8B3AA] text-sm leading-relaxed max-w-2xl">
              Pumps, algae, finishes, leaves in the basin: regular upkeep keeps
              small problems small. Rather not do it yourself? Chris handles
              maintenance visits too.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 self-start lg:self-auto">
            <button
              type="button"
              onClick={() => setShowMaintenance(true)}
              data-testid="request-maintenance-button"
              className="inline-flex items-center justify-center gap-2.5 px-6 py-3 bg-[#7A6547] text-[#F9F6F0] text-[11px] tracking-[0.22em] uppercase hover:bg-[#5E4D35] transition-colors whitespace-nowrap"
            >
              <Wrench size={14} strokeWidth={1.5} />
              Request Maintenance
            </button>
          </div>
        </div>
        <ReserveModal
          open={showMaintenance}
          onClose={() => setShowMaintenance(false)}
          piece="Fountain or statue maintenance"
          mode="maintenance"
        />
      </div>
    </section>
  );
}
